import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, BellRing, MessageCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { formatRelative } from '@/lib/utils';
import { OUTBOUND_KEY, processesApi, type OutboundMessage } from './processes-api';
import { useCanEdit } from '@/lib/auth';

const REASONS = [
  'task_created',
  'task_closed',
  'quote_reminder',
  'monthly_invoice',
  'monthly_ready',
  'unbilled',
  'quote_signed_copy',
  'external_invoice_failed',
  'missing_tax_id',
  'task_scheduled',
  'customer_confirmed',
  'customer_reschedule',
  'quote_planned',
  'contract_notice',
  'contract_warranty',
  'purchase_order',
  'bill_approve',
  'bill_due',
  'bill_overdue',
] as const;
type Reason = (typeof REASONS)[number];
const isReason = (r: string): r is Reason => (REASONS as readonly string[]).includes(r);

/**
 * "לשלוח היום" — מה שתהליך יצר ולא יכול היה לצאת לבד: ללקוח בלי מייל, או
 * כש-Gmail לא מחובר. לחיצה פותחת WhatsApp עם ההודעה מוכנה, ומסמנת "נשלח".
 */
export function OutboundQueue({ compact = false }: { compact?: boolean }) {
  const canEdit = useCanEdit();
  const { t } = useTranslation(['processes', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const queue = useQuery({
    queryKey: OUTBOUND_KEY,
    queryFn: ({ signal }) => processesApi.queue(signal),
  });

  const settle = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'sent' | 'dismiss' }) =>
      processesApi.settle(id, action),
    onSuccess: () => void qc.invalidateQueries({ queryKey: OUTBOUND_KEY }),
    onError: (err: unknown) =>
      toast.error(t('outbound.updateFailed'), err instanceof ApiError ? err.message : undefined),
  });

  const items = queue.data ?? [];
  if (compact && items.length === 0) return null;

  const open = (m: OutboundMessage) => {
    if (m.waUrl) window.open(m.waUrl, '_blank', 'noopener,noreferrer');
    settle.mutate({ id: m.id, action: 'sent' });
  };

  return (
    <Card>
      <CardHeader className="flex items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <BellRing className="size-4 text-accent" aria-hidden />
            {t('outbound.title')}
          </CardTitle>
          <CardDescription>{t('outbound.description')}</CardDescription>
        </div>
        {items.length > 0 ? <Badge tone="accent">{items.length}</Badge> : null}
      </CardHeader>
      <CardContent className="p-0">
        {queue.isPending ? (
          <div className="p-4">
            <Skeleton className="h-16" />
          </div>
        ) : items.length === 0 ? (
          <p className="px-5 py-6 text-center text-sm text-fg-muted">
            {t('outbound.empty')}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {items.slice(0, compact ? 5 : 100).map((m) => (
              <li key={m.id} className="flex flex-wrap items-start gap-3 px-5 py-3">
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-fg">
                    {m.customer?.name ?? t('outbound.attention')}
                    <Badge
                      tone={
                        m.reason === 'unbilled' || m.status === 'FAILED' ? 'warning' : 'neutral'
                      }
                      dot={false}
                    >
                      {isReason(m.reason) ? t(`outbound.reasons.${m.reason}`) : m.reason}
                    </Badge>
                  </p>
                  <p className="mt-0.5 line-clamp-2 text-xs text-fg-muted">{m.body}</p>
                  {m.status === 'FAILED' && m.error ? (
                    <p className="mt-1 flex items-center gap-1 text-2xs text-warning">
                      <AlertTriangle className="size-3" aria-hidden />
                      {m.error}
                    </p>
                  ) : null}
                  <p className="mt-1 text-2xs text-fg-subtle">{formatRelative(m.createdAt)}</p>
                </div>
                {canEdit ? (
                  <div className="flex items-center gap-1.5">
                    {m.waUrl ? (
                      <Button size="sm" onClick={() => open(m)} disabled={settle.isPending}>
                        <MessageCircle aria-hidden />
                        WhatsApp
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={m.waUrl ? t('outbound.skip') : t('outbound.markHandled')}
                      disabled={settle.isPending}
                      onClick={() => settle.mutate({ id: m.id, action: 'dismiss' })}
                    >
                      <X aria-hidden />
                      {m.waUrl ? null : t('outbound.handled')}
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
